/**
 * Intercom Messenger Integration with JWT Identity Verification
 * 
 * This module provides utilities for integrating Intercom live chat.
 * - Uses JWT identity verification (intercom_user_jwt from server)
 * - Launcher is hidden by default - chat opens only via explicit calls
 * - Boots lazily (only when user clicks Live Chat)
 */

import Intercom, { show as intercomShow, showNewMessage as intercomShowNewMessage, update as intercomUpdate, shutdown as intercomShutdown } from '@intercom/messenger-js-sdk';
import { supabase } from './supabase';

// Configuration
const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID as string | undefined;
const IS_DEV = import.meta.env.DEV;

// Track initialization state
let isBooted = false;
let currentUserId: string | null = null;
let bootPromise: Promise<boolean> | null = null;

// The SDK exports functions directly - no need to check window.Intercom
// We can use the imported functions: show, showNewMessage, update, shutdown

/**
 * Check if Intercom is properly configured
 */
export function isIntercomConfigured(): boolean {
  return Boolean(INTERCOM_APP_ID);
}

/**
 * Fetch JWT from server for identity verification
 * Uses supabase.functions.invoke() to ensure proper authentication headers
 */
async function fetchIntercomJWT(): Promise<{
  user_id: string;
  email: string | null;
  name: string | null;
  created_at: number | undefined;
  intercom_user_jwt: string;
} | null> {
  try {
    if (IS_DEV) {
      console.log('[Intercom] 🔵 Live Chat button clicked - fetching JWT from edge function...');
    }
    
    // Get session to extract user and access token in one call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token || !session?.user) {
      if (IS_DEV) {
        console.error('[Intercom] ❌ No session or token available:', sessionError?.message || 'No access_token or user');
      }
      return null;
    }
    
    const user = session.user;
    
    if (IS_DEV) {
      console.log('[Intercom] ✅ User authenticated:', {
        userId: user.id,
        email: user.email,
        name: user.user_metadata?.full_name || user.user_metadata?.name || 'N/A',
      });
      console.log('[Intercom] 📡 Requesting intercom-jwt with Authorization Bearer', session.access_token.substring(0, 12) + '...');
    }
    
    // Use supabase.functions.invoke() with explicit headers to guarantee Authorization and apikey are sent
    // This ensures the POST request includes both Bearer token and apikey, reaching the function code
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const { data, error } = await supabase.functions.invoke('intercom-jwt', {
      body: {
        user_id: user.id,
        email: user.email || undefined,
        name: user.user_metadata?.full_name || user.user_metadata?.name || 
              (user.user_metadata?.first_name ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim() : undefined),
      },
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': anonKey || '',
        'Content-Type': 'application/json',
      },
    });
    
    if (error) {
      if (IS_DEV) {
        console.error('[Intercom] ❌ Edge function error:', {
          status: error.status || 'unknown',
          message: error.message,
          context: error.context,
        });
        // Log full error details for debugging
        console.error('[Intercom] Full error object:', error);
      }
      return null;
    }
    
    if (IS_DEV) {
      console.log('[Intercom] ✅ Edge function response received:', {
        status: 200,
        hasJWT: !!data?.intercom_user_jwt,
        userId: data?.user_id,
      });
    }

    if (!data || !data.intercom_user_jwt) {
      console.error('[Intercom] Invalid JWT response - missing intercom_user_jwt:', data);
      return null;
    }

    return data;
  } catch (err) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Exception fetching JWT:', err);
    }
    return null;
  }
}

/**
 * Boot Intercom with JWT identity verification
 * This is called lazily when user clicks Live Chat
 */
async function bootIntercom(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  if (!INTERCOM_APP_ID) {
    if (IS_DEV) {
      console.warn('[Intercom] VITE_INTERCOM_APP_ID not configured - live chat disabled');
    }
    return false;
  }

  // If already booted, return true
  if (isBooted) {
    if (IS_DEV) {
      console.log('[Intercom] Already booted');
    }
    return true;
  }

  // Fetch JWT from server - REQUIRED for secure identity verification
  const userData = await fetchIntercomJWT();
  
  if (!userData) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Could not get JWT - Live chat unavailable (auth required)');
    }
    // Do NOT boot without JWT - this enforces secure Messenger only
    return false;
  }

  // If user changed, shutdown first
  if (currentUserId && currentUserId !== userData.user_id) {
    shutdownIntercom();
  }

  try {
    if (IS_DEV) {
      console.log('[Intercom] 🚀 Booting Intercom SDK with config:', {
        app_id: INTERCOM_APP_ID,
        user_id: userData.user_id,
        hasEmail: !!userData.email,
        hasName: !!userData.name,
        hasJWT: !!userData.intercom_user_jwt,
      });
    }

    // Use the default Intercom() function to initialize and load scripts
    // This is what actually loads the Intercom widget from their CDN
    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: userData.user_id,
      email: userData.email || undefined,
      name: userData.name || undefined,
      created_at: userData.created_at,
      user_hash: userData.intercom_user_jwt, // JWT for identity verification
      hide_default_launcher: true,
    });

    if (IS_DEV) {
      console.log('[Intercom] ✅ SDK boot() called successfully');
    }

    // Note: We don't wait here anymore - waitForIntercomWidgetReady() is called
    // in ensureIntercomBooted() after booting completes

    isBooted = true;
    currentUserId = userData.user_id;

    if (IS_DEV) {
      console.log('[Intercom] ✅ Booted with JWT identity verification for user:', userData.user_id);
      console.log('[Intercom] 🚀 Ready to show messenger');
    }
    return true;
  } catch (error) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Failed to boot:', error);
    }
    return false;
  }
}

/**
 * Wait for Intercom widget to be fully loaded and ready
 * Checks for the widget in DOM and waits up to 5 seconds
 */
async function waitForIntercomWidgetReady(): Promise<boolean> {
  let attempts = 0;
  const maxAttempts = 50; // 5 seconds total (50 * 100ms)
  
  while (attempts < maxAttempts) {
    // Check multiple ways the widget might be present
    const intercomFrame = document.querySelector('iframe[title*="Intercom"], iframe[id*="intercom"], iframe[src*="intercom"]');
    const intercomContainer = document.querySelector('.intercom-lightweight-app, #intercom-container, [class*="intercom"]');
    const hasWindowIntercom = typeof (window as any).Intercom === 'function';
    
    // Also check if Intercom scripts are loaded by looking for the global Intercom object
    // The SDK might expose it differently
    const intercomReady = intercomFrame || intercomContainer || hasWindowIntercom;
    
    if (intercomReady) {
      if (IS_DEV) {
        console.log('[Intercom] ✅ Widget is ready:', {
          hasFrame: !!intercomFrame,
          hasContainer: !!intercomContainer,
          hasWindowIntercom,
          attempt: attempts + 1,
        });
      }
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  
  if (IS_DEV) {
    console.warn('[Intercom] ⚠️ Widget not ready after 5s');
  }
  return false;
}

/**
 * Ensure Intercom is booted (lazy initialization)
 * Returns a promise that resolves to true if booted successfully
 */
export async function ensureIntercomBooted(): Promise<boolean> {
  // If already booting, wait for that
  if (bootPromise) {
    const booted = await bootPromise;
    if (booted) {
      // Wait for widget to be ready after booting
      await waitForIntercomWidgetReady();
    }
    return booted;
  }

  // Start boot process
  bootPromise = bootIntercom();
  const result = await bootPromise;
  
  // Clear promise if failed so we can retry
  if (!result) {
    bootPromise = null;
    return false;
  }
  
  // Wait for widget to be ready after booting
  await waitForIntercomWidgetReady();
  
  return result;
}

/**
 * Shutdown Intercom (on logout)
 * Clears user session to prevent data leakage between users
 */
export function shutdownIntercom(): void {
  if (typeof window === 'undefined') return;
  
  if (!isBooted) return;

  try {
    intercomShutdown();
    
    isBooted = false;
    currentUserId = null;
    bootPromise = null;

    if (IS_DEV) {
      console.log('[Intercom] Shutdown successfully');
    }
  } catch (error) {
    if (IS_DEV) {
      console.error('[Intercom] Failed to shutdown:', error);
    }
  }
}

/**
 * Open Intercom messenger programmatically
 * Will boot Intercom first if not already booted
 */
export async function openIntercom(): Promise<void> {
  if (typeof window === 'undefined') return;
  
  if (!INTERCOM_APP_ID) {
    if (IS_DEV) {
      console.warn('[Intercom] Cannot open - not configured');
    }
    return;
  }

  // Check if user is authenticated before proceeding
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    if (IS_DEV) {
      console.warn('[Intercom] User not authenticated - cannot open');
    }
    alert('Please sign in to use Live Chat.');
    return;
  }

  if (IS_DEV) {
    console.log('[Intercom] 🟢 Intercom button clicked in Navbar');
  }

  // Ensure booted first
  const booted = await ensureIntercomBooted();
  if (!booted) {
    if (IS_DEV) {
      console.warn('[Intercom] Failed to boot, cannot open');
    }
    alert('Unable to open Live Chat. Please try again.');
    return;
  }

  try {
    // Widget should already be ready from ensureIntercomBooted()
    // But add a small delay to be safe
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (IS_DEV) {
      console.log('[Intercom] 🎯 Calling intercomShow()...');
    }
    
    // Use the SDK's exported show function directly
    intercomShow();
    
    if (IS_DEV) {
      console.log('[Intercom] ✅ intercomShow() called');
    }
  } catch (error) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Error calling intercomShow():', error);
      console.error('[Intercom] Error details:', {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
    }
    alert('Failed to open Live Chat. Please try again.');
  }
}

/**
 * Open live chat with project context
 * Use this when user clicks the Live Chat button on property details
 */
export interface LiveChatContext {
  projectId?: string | number;
  projectName?: string;
  developerName?: string;
  unitId?: string;
  pageUrl?: string;
}

export async function openLiveChat(context: LiveChatContext): Promise<void> {
  if (typeof window === 'undefined') return;
  
  if (!INTERCOM_APP_ID) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Cannot open live chat - VITE_INTERCOM_APP_ID not configured');
    }
    alert('Live Chat is not configured. Please contact support.');
    return;
  }

  if (IS_DEV) {
    console.log('[Intercom] 🟢 Live Chat button clicked on Project Details page');
    console.log('[Intercom] Config check:', {
      hasAppId: !!INTERCOM_APP_ID,
      appIdLength: INTERCOM_APP_ID?.length || 0,
      hasShowFunction: typeof intercomShow === 'function',
      hasShowNewMessageFunction: typeof intercomShowNewMessage === 'function',
    });
  }

  // Check if user is authenticated before proceeding
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    if (IS_DEV) {
      console.warn('[Intercom] User not authenticated - cannot open live chat');
    }
    // Show friendly message to user
    alert('Please sign in to use Live Chat.');
    return;
  }

  // Ensure booted first (this will fetch JWT and boot Intercom)
  const booted = await ensureIntercomBooted();
  if (!booted) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Failed to boot - Live chat unavailable (auth)');
    }
    alert('Live chat unavailable. Please ensure you are signed in and try again.');
    return;
  }

  try {
    // Widget should already be ready from ensureIntercomBooted()
    // But add a small delay to be safe
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (IS_DEV) {
      console.log('[Intercom] 🎯 Updating Intercom with context and showing messenger...');
    }

    // Update Intercom with context before showing
    try {
      intercomUpdate({
        project_id: context.projectId?.toString() || undefined,
        project_name: context.projectName || undefined,
        developer_name: context.developerName || undefined,
        unit_id: context.unitId?.toString() || undefined,
        page_url: context.pageUrl || window.location.href,
      });
      if (IS_DEV) {
        console.log('[Intercom] ✅ Context updated');
      }
    } catch (updateError) {
      if (IS_DEV) {
        console.warn('[Intercom] ⚠️ Failed to update context (non-critical):', updateError);
      }
      // Continue even if update fails
    }

    // Show messenger with pre-filled message about the project
    if (context.projectName) {
      if (IS_DEV) {
        console.log('[Intercom] 🎯 Calling intercomShowNewMessage()...');
      }
      intercomShowNewMessage(`Hi, I have a question about ${context.projectName}`);
    } else {
      if (IS_DEV) {
        console.log('[Intercom] 🎯 Calling intercomShow()...');
      }
      intercomShow();
    }

    if (IS_DEV) {
      console.log('[Intercom] ✅ Intercom messenger opened with context:', context);
    }
  } catch (error) {
    if (IS_DEV) {
      console.error('[Intercom] ❌ Failed to open live chat:', error);
      console.error('[Intercom] Error details:', {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        context,
      });
    }
    alert('Failed to open Live Chat. Please try again.');
  }
}

// Legacy function for backward compatibility - now async
export function initIntercom(_user: {
  id: string;
  email?: string | null;
  name?: string | null;
  createdAt?: string | null;
}): void {
  // No-op - we now boot lazily on demand
  if (IS_DEV) {
    console.log('[Intercom] initIntercom called - Intercom will boot lazily on first chat open');
  }
}
